import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ApiClient } from '../../api'
import { useMe } from '../../hooks/useMe'
import { C, F } from './theme'
import { Card, SectionHead, Btn, Bar, Icon, LockedCard, gbp, Pill } from './prims'
import type { ForecastFinancePayload } from '../../types'

// EM-9 · Event Finance "Forecast" view. A read-only mirror of the committed
// forecast scenario vs the forecast baseline plan. Sits as a sibling tab beside
// Budget (Plan) and Financials (Actuals) — the repo's established convention.
// HANDOFF-DEVIATION: the reference prototype folds Budget/Actuals/Forecast into
// one segmented control; this repo already splits Plan (Budget tab) and Actuals
// (Financials tab) into separate EventDetail tabs, so Forecast lands as a third
// sibling tab rather than an in-tab segment. Content matches the prototype.

function pct(cur: number, base: number): number {
  if (!base) return 0
  return Math.round(((cur - base) / base) * 100)
}

export function ForecastFinanceView({ eventId }: { eventId: number }) {
  const { data: me } = useMe()
  const q = useQuery({
    queryKey: ['forecast-finance', eventId],
    queryFn: () => ApiClient.forecastFinance(eventId),
    retry: false,
    enabled: Number.isFinite(eventId) && eventId > 0
  })

  if (me && !me.hasFinancial) return <LockedCard label="Forecast financials need financial access" />
  if (q.isLoading) return <div style={{ fontFamily: F.mono, fontSize: 13, color: C.inkSoft }}>Loading forecast…</div>
  if (q.isError) {
    if ((q.error as Error).message.startsWith('403')) return <LockedCard label="Forecast financials need financial access" />
    return <div style={{ fontFamily: F.mono, fontSize: 13, color: C.oxblood }}>Could not load the forecast.</div>
  }
  const fin = q.data as ForecastFinancePayload
  if (!fin.hasForecast) {
    return (
      <Card style={{ textAlign: 'center', padding: 30, fontFamily: F.body }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, borderRadius: 12, background: C.brassBg, marginBottom: 10 }}>
          <Icon name="chart" size={20} color={C.brass} />
        </div>
        <div style={{ fontFamily: F.serif, fontSize: 18, fontWeight: 800, color: C.ink, marginBottom: 4 }}>No forecast yet</div>
        <div style={{ fontFamily: F.body, fontSize: 13, color: C.inkSoft }}>This event was not modelled in Forecast Studio, so there is no forecast to mirror here.</div>
      </Card>
    )
  }

  const revDelta = pct(fin.grossRevenuePence, fin.plan.revenuePence)
  const costDelta = pct(fin.totalCostsPence, fin.plan.costsPence)
  const netDelta = fin.netPence - fin.plan.netPence
  const ahead = fin.netPence >= fin.plan.netPence

  return (
    <div style={{ fontFamily: F.body, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: C.greenBg, border: `1px solid ${C.greenSoft}`, borderRadius: 10, padding: '10px 14px', flexWrap: 'wrap' }}>
        <Icon name="sparkle" size={15} color={C.green} />
        <span style={{ fontFamily: F.body, fontSize: 13, fontWeight: 700, color: C.green }}>Pulled live from Forecast Studio</span>
        <span style={{ marginLeft: 'auto' }}>
          <Link to={`/forecast/${eventId}`} style={{ textDecoration: 'none' }}><Btn variant="soft" small icon="chart">Open in Studio</Btn></Link>
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))', gap: 12 }}>
        <Kpi accent={C.green} label="Forecast Revenue" value={gbp(fin.grossRevenuePence)} trend={`${revDelta >= 0 ? '+' : ''}${revDelta}% vs plan`} up={revDelta >= 0} />
        <Kpi accent={C.oxblood} label="Forecast Costs" value={gbp(fin.totalCostsPence)} trend={`${costDelta >= 0 ? '+' : ''}${costDelta}% vs plan`} up={costDelta <= 0} />
        <Kpi accent={C.brass} label="Forecast Net" value={gbp(fin.netPence)} trend={`${netDelta >= 0 ? '+' : '−'}${gbp(Math.abs(netDelta))} vs plan`} up={netDelta >= 0} />
        <Kpi accent={C.slate} label="Net Incremental" value={gbp(fin.netIncrementalPence)} trend="vs displaced normal days" up={fin.netIncrementalPence >= 0} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(330px,1fr))', gap: 16, alignItems: 'start' }}>
        <Card>
          <SectionHead icon="pound" label="Revenue by stream · forecast vs plan" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {fin.streams.map((s) => {
              const max = Math.max(s.forecastPence, s.planPence) || 1
              return (
                <div key={s.label}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontFamily: F.body, fontSize: 12, fontWeight: 700, color: C.inkSoft }}>{s.label}</span>
                    <span style={{ fontFamily: F.mono, fontSize: 12, fontWeight: 700, color: C.ink }}>{gbp(s.forecastPence)}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Bar pct={(s.forecastPence / max) * 100} color={C.green} h={7} />
                    <Bar pct={(s.planPence / max) * 100} color={C.brassLt} h={7} />
                  </div>
                </div>
              )
            })}
          </div>
          <div style={{ display: 'flex', gap: 14, marginTop: 12, fontFamily: F.mono, fontSize: 10, color: C.inkFaint }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 6, background: C.green, borderRadius: 2 }} /> forecast</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 6, background: C.brassLt, borderRadius: 2 }} /> plan</span>
          </div>
        </Card>

        <Card>
          <SectionHead icon="trend" label="Plan → forecast bridge" />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <BridgeRow label="Plan net" value={fin.plan.netPence} />
            <BridgeRow label="+ Revenue upside" value={fin.grossRevenuePence - fin.plan.revenuePence} signed />
            <BridgeRow label="− Cost movement" value={-(fin.totalCostsPence - fin.plan.costsPence)} signed />
            <BridgeRow label="Forecast net" value={fin.netPence} bold />
          </div>
          <div style={{ marginTop: 12, padding: '9px 12px', borderRadius: 9, background: ahead ? C.greenBg : C.amberSoft, color: ahead ? C.green : C.amber, fontFamily: F.body, fontSize: 12.5, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 7 }}>
            <Icon name={ahead ? 'checkc' : 'alert'} size={14} />
            {ahead ? 'Forecast is ahead of the plan.' : 'Forecast is below the plan.'}
          </div>
          <div style={{ marginTop: 12 }}>
            <Pill fg={C.slate} bg={C.slateSoft} icon="info">Plan = the forecast baseline intent; a committed event grows a real budget over time.</Pill>
          </div>
        </Card>
      </div>
    </div>
  )
}

function Kpi({ accent, label, value, trend, up }: { accent: string; label: string; value: string; trend: string; up?: boolean }) {
  return (
    <div style={{ position: 'relative', background: C.card, border: `1px solid ${C.lineSoft}`, borderRadius: 11, padding: '16px 18px', overflow: 'hidden' }}>
      <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: accent }} />
      <div style={{ fontFamily: F.body, fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: C.inkSoft, marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: F.serif, fontSize: 25, fontWeight: 800, color: C.ink, lineHeight: 1 }}>{value}</div>
      <div style={{ fontFamily: F.body, fontSize: 12, fontWeight: 700, marginTop: 6, color: up === false ? C.oxblood : up ? C.green : C.inkSoft }}>{trend}</div>
    </div>
  )
}

function BridgeRow({ label, value, bold, signed }: { label: string; value: number; bold?: boolean; signed?: boolean }) {
  const txt = (signed && value >= 0 ? '+' : signed && value < 0 ? '−' : '') + gbp(signed ? Math.abs(value) : value)
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderTop: bold ? `1px solid ${C.lineSoft}` : undefined }}>
      <span style={{ fontFamily: F.body, fontSize: 12.5, fontWeight: bold ? 800 : 600, color: bold ? C.ink : C.inkSoft }}>{label}</span>
      <span style={{ fontFamily: F.mono, fontSize: 13, fontWeight: bold ? 800 : 600, color: C.ink }}>{txt}</span>
    </div>
  )
}
